/**
 * Nível de acesso por módulo, como o backend devolve em `/auth/me` para cada empresa
 * (`access: { nfse, nfe, nfce, documents }`). `NONE` esconde o módulo, `READ` permite ver
 * e `FULL` permite operar. O servidor continua sendo quem nega (403 `MODULE_DENIED` /
 * `MODULE_READ_ONLY`); aqui só decidimos o que mostrar.
 */
import type { Company } from '~/api/auth';

export type AccessLevel = Company['access'][keyof Company['access']];
export type ModuleKey = keyof Company['access'];
export type CompanyRole = Company['role'];

export const ACCESS_LEVEL = {
  none: 'NONE',
  read: 'READ',
  full: 'FULL',
} as const satisfies Record<string, AccessLevel>;

/** Do menor para o maior: é a ordem em que as telas oferecem os níveis. */
export const ACCESS_LEVEL_ORDER = [
  'NONE',
  'READ',
  'FULL',
] as const satisfies readonly AccessLevel[];

export const COMPANY_ROLE = {
  admin: 'ADMIN',
  member: 'MEMBER',
} as const satisfies Record<string, CompanyRole>;

/** Os papéis na empresa, na ordem em que as telas os oferecem. */
export const COMPANY_ROLES = ['ADMIN', 'MEMBER'] as const satisfies readonly CompanyRole[];

const ACCESS_LEVEL_RANK: Record<AccessLevel, number> = {
  NONE: 0,
  READ: 10,
  FULL: 20,
};

export const ACCESS_LEVEL_LABEL: Record<AccessLevel, string> = {
  NONE: 'Sem acesso',
  READ: 'Consulta',
  FULL: 'Completo',
};

export const MODULE_LABEL: Record<ModuleKey, string> = {
  nfse: 'NFS-e',
  nfe: 'NF-e',
  nfce: 'NFC-e',
  documents: 'Documentos',
  company_users: 'Usuários',
};

/**
 * Os módulos que são acesso a dado fiscal, na ordem em que as telas os mostram.
 * `company_users` fica fora: é gestão da equipe da empresa, não "o seu acesso".
 */
export const FISCAL_MODULES = [
  'nfse',
  'nfe',
  'nfce',
  'documents',
] as const satisfies readonly ModuleKey[];

/** Os módulos que se concedem num vínculo: os fiscais. `company_users` vem do papel. */
export type FiscalModuleKey = (typeof FISCAL_MODULES)[number];

export const COMPANY_ROLE_LABEL: Record<CompanyRole, string> = {
  ADMIN: 'Administrador da empresa',
  MEMBER: 'Funcionário',
};

export function atLeast(level: AccessLevel | undefined, required: AccessLevel): boolean {
  if (!level) return false;
  return ACCESS_LEVEL_RANK[level] >= ACCESS_LEVEL_RANK[required];
}

/** Nível do usuário em um módulo da empresa; sem empresa não há acesso a nada. */
export function companyLevel(company: Company | undefined, module: ModuleKey): AccessLevel {
  return company?.access[module] ?? ACCESS_LEVEL.none;
}
